/**
 * Levene 検定（等分散性）
 * 各群の |x_ij - 中心値| に対する一元配置 ANOVA（Brown–Forsythe 型は中央値を中心に使用）
 * 計算式は docs/systems/keisuke/FORMULAS.md を参照
 */

import { oneWayANOVA } from './anova';

export interface LeveneTestResult {
  statistic: number;
  dfBetween: number;
  dfWithin: number;
  pValue: number;
  center: 'median' | 'mean';
}

/**
 * @param groups 群ごとの数値配列（各群 n >= 1）
 * @param useMedian true のとき Brown–Forsythe（中央値中心）。false のとき古典的 Levene（平均中心）
 */
export function leveneTest(groups: number[][], useMedian = false): LeveneTestResult {
  const k = groups.length;
  if (k < 2) {
    throw new Error('Levene検定には2群以上必要です');
  }

  const rows: { group: string; value: number }[] = [];
  groups.forEach((values, gi) => {
    if (values.length === 0) {
      throw new Error(`群 ${gi + 1} のデータが空です`);
    }
    const center = useMedian ? median(values) : mean(values);
    values.forEach((v) => {
      if (!Number.isFinite(v)) return;
      rows.push({ group: String(gi), value: Math.abs(v - center) });
    });
  });

  if (rows.length < k + 1) {
    throw new Error('Levene検定には有効なデータが不足しています');
  }

  const anova = oneWayANOVA(rows);
  return {
    statistic: anova.f,
    dfBetween: anova.dfBetween,
    dfWithin: anova.dfWithin,
    pValue: anova.pValue,
    center: useMedian ? 'median' : 'mean',
  };
}

function mean(values: number[]): number {
  return values.reduce((s, v) => s + v, 0) / values.length;
}

function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const n = sorted.length;
  const mid = Math.floor(n / 2);
  return n % 2 === 0 ? (sorted[mid - 1]! + sorted[mid]!) / 2 : sorted[mid]!;
}
